/**
 * ContactModalConstants.js - Step 1: Extract Constants and Configuration
 * -------------------------------
 * Location: src/scripts/contact/core/ContactModalConstants.js
 * 
 * Centralized constants for the ContactModal system to replace magic numbers,
 * strings, and configuration scattered throughout the original code.
 */

import { orgContactPhrase } from '../../utils/orgContact.js';

// Constants for validation, UI, and configuration
const CONSTANTS = {
  // DOM selectors
  SELECTORS: {
    CONTACT_MODAL: 'contact-modal',
    CONTACT_FORM: 'contact-form',
    MODAL_CLOSE: '.modal-close, .modal-cancel',
    SUBMIT_BUTTON: 'button[type="submit"]',
    FORM_INPUTS: 'input, textarea',
    FIRST_INPUT: 'input:not([type="hidden"]), textarea',
    FLASH_CONTAINER: '.flash-messages-container',
    FLASH_MESSAGE: '.flash-message',
    FIELD_ERROR: '.field-error',
    FORM_MESSAGE: '.form-message',
    STATIC_CHECKBOXES: 'static-checkboxes',
    DYNAMIC_CHECKBOXES: 'dynamic-checkboxes',
    MODAL_TYPE_FIELD: 'modal-type',
    MODAL_TITLE: '#modal-title'
  },

  // CSS classes
  CLASSES: {
    MODAL_OPEN: 'modal-open',
    FIELD_ERROR: 'field-error',
    FLASH_MESSAGE: 'flash-message',
    FLASH_SHOW: 'flash-message-show',
    FLASH_HIDE: 'flash-message-hide',
    REQUIRED: 'required',
    CHECKBOX_LABEL: 'checkbox-label'
  },

  // Default validation rules
  VALIDATION_DEFAULTS: {
    MESSAGE: {
      MIN_LENGTH: 0,
      MAX_LENGTH: 2000,
      REQUIRED: false
    },
    FIRST_NAME: {
      MIN_LENGTH: 2,
      MAX_LENGTH: 50,
      REQUIRED: true
    },
    LAST_NAME: {
      MIN_LENGTH: 0,
      MAX_LENGTH: 50,
      REQUIRED: false
    },
    EMAIL: {
      MAX_LENGTH: 254,
      REQUIRED: true
    },
    PHONE: {
      REQUIRED: false
    }
  },

  // Regular expressions
  REGEX: {
    NAME_PATTERN: /^[a-zA-Z\s\-']+$/,
    PHONE_CLEAN: /[^\d+()-]/g,
    WHITESPACE: /\s+/g
  },

  // Timing constants
  TIMING: {
    FOCUS_DELAY: 100,
    FLASH_HIDE_DELAY: 300,
    ERROR_MESSAGE_TIMEOUT: 10000,
    FLASH_DURATION: 5000,
    ERROR_FLASH_DURATION: 8000
  },

  // Error handling
  ERROR_HANDLER: {
    MODULE_NAME: 'ContactModal',
    MAX_ERRORS: 15
  },

  // Form submission
  FORM_SUBMISSION: {
    DEFAULT_ENDPOINTS: ['/contact'], // Try /contact first (only endpoint that exists)
    SOURCE: 'contact_modal',
    TYPE: 'contact_inquiry'
  }
};

// Default configuration objects
const DEFAULT_SUBMITTER_OPTIONS = {
  endpoints: CONSTANTS.FORM_SUBMISSION.DEFAULT_ENDPOINTS,
  debug: false,
  csrf: {
    enabled: false // Disable CSRF for modal forms (app doesn't use CSRF tokens)
  },
  headers: {
    'Accept': 'application/json' // Ensure server knows we want JSON response
  },
  transformFields: {
    firstName: (value) => value.trim(),
    lastName: (value) => value.trim(),
    email: (value) => value.trim().toLowerCase(),
    phone: (value) => value.replace(CONSTANTS.REGEX.WHITESPACE, '').replace(CONSTANTS.REGEX.PHONE_CLEAN, ''),
    message: (value) => value.trim()
  },
  additionalData: {
    source: CONSTANTS.FORM_SUBMISSION.SOURCE,
    type: CONSTANTS.FORM_SUBMISSION.TYPE
  }
};

const DEFAULT_FLASH_OPTIONS = {
  position: 'top-right',
  duration: CONSTANTS.TIMING.FLASH_DURATION,
  errorDuration: CONSTANTS.TIMING.ERROR_FLASH_DURATION,
  showIcon: true,
  showCloseButton: true,
  stackMessages: true
};

// Field validation messages
const VALIDATION_MESSAGES = {
  FIRST_NAME: {
    REQUIRED: 'First name is required',
    MIN_LENGTH: (min) => `First name must be at least ${min} characters`,
    MAX_LENGTH: (max) => `First name must be less than ${max} characters`,
    PATTERN: 'First name can only contain letters, spaces, hyphens, and apostrophes'
  },
  LAST_NAME: {
    REQUIRED: 'Last name is required',
    MIN_LENGTH: (min) => `Last name must be at least ${min} characters`,
    MAX_LENGTH: (max) => `Last name must be less than ${max} characters`,
    PATTERN: 'Last name can only contain letters, spaces, hyphens, and apostrophes'
  },
  EMAIL: {
    REQUIRED: 'Email is required',
    INVALID: 'Please enter a valid email address',
    MAX_LENGTH: (max) => `Email address must be less than ${max} characters`
  },
  PHONE: {
    REQUIRED: 'Phone number is required',
    INVALID: 'Please enter a valid phone number'
  },
  MESSAGE: {
    REQUIRED: 'Message is required',
    MIN_LENGTH: (min) => `Message must be at least ${min} characters`,
    MAX_LENGTH: (max) => `Message must be less than ${max} characters`
  }
};

// Flash message icons
const FLASH_ICONS = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ'
};

// Error message mapping
// `server` and `default` include a fallback contact path — these are the
// categories where retrying within the app is unlikely to be the fix, so a
// visitor shouldn't be left with no path forward if the form itself won't
// cooperate. `duplicate`/`validation`/`timeout` are left as-is since those
// genuinely are self-resolvable by the visitor (wait a moment, fix a field,
// check the connection) and don't need the same escalation. The contact path
// is read from <body> when the message is shown (getters), not at import.
const ERROR_MESSAGES = {
  duplicate: 'This form was already submitted recently. Please wait before submitting again.',
  validation: 'Please check your information and try again.',
  timeout: 'The request timed out. Please check your connection and try again.',
  get server() { return `Our servers are experiencing issues. Please try again in a few minutes, or reach us directly at ${orgContactPhrase()}.`; },
  csrf: 'Security token invalid. Please refresh the page and try again.',
  get default() { return `Sorry, there was an error sending your message. Please try again, or reach us directly at ${orgContactPhrase()}.`; }
};

// Export all constants and configurations
export {
  CONSTANTS,
  DEFAULT_SUBMITTER_OPTIONS,
  DEFAULT_FLASH_OPTIONS,
  VALIDATION_MESSAGES,
  FLASH_ICONS,
  ERROR_MESSAGES
};